import {useCallback, useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {ReactComponent as CheckIcon} from '@fortawesome/fontawesome-free/svgs/solid/check.svg'
import {ReactComponent as CopyIcon} from '@fortawesome/fontawesome-free/svgs/regular/copy.svg'

import {Modal} from '../../../../_metronic/elements/Modal'
import {KTSVG} from '../../../../_metronic/helpers'
import * as LandService from '../Fields/services/LandService'
import {
  buildCopilotResultInterpretation,
  getAgronomicProfileForQuestion,
  getCopilotActionLabelKey,
  getCopilotFindingTone,
  groupCopilotFindings,
  hasAvailableAgronomicAnalysis,
  hasCompleteAgronomicProfile,
  normalizeCopilotAnalysis,
} from './copilotModel'
import ChatGptLogo from './ChatGptLogo'
import SpecificCopilotAgronomy, {EMPTY_PROFILE} from './SpecificCopilotAgronomy'
import SpecificCopilotIntegratedAnalysis from './SpecificCopilotIntegratedAnalysis'
import {downloadSpecificCopilotMarkdown} from './specificCopilotMarkdown'
import {downloadSpecificCopilotPdf} from './specificCopilotPdf'
import './SpecificCopilot.scss'

const formatAnalysisTimestamp = (value, language) => {
  const date = new Date(value)
  if (!value || Number.isNaN(date.getTime())) {
    return ''
  }

  return new Intl.DateTimeFormat(language || 'es', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

const AnalysisFreshness = ({analysis, loading, checking, onRefresh}) => {
  const {t, i18n} = useTranslation('copilot')
  const metadata = analysis?.analysisMetadata || {}
  const freshness = metadata.sourceFreshness || {status: 'unknown', changes: []}
  const status = freshness.status || 'unknown'
  const analyzedAt = formatAnalysisTimestamp(metadata.analyzedAt, i18n.language)
  const persistenceStatus = metadata.persistence?.status || 'legacy_unknown'
  const changedSources = (freshness.changes || []).map((sourceId) =>
    t(`freshness.sources.${sourceId}`, {defaultValue: sourceId})
  )

  if (!metadata.reused && !['saved', 'legacy_unknown'].includes(persistenceStatus)) {
    return null
  }

  return (
    <section
      className={`specific-copilot-freshness specific-copilot-freshness--${status}`}
      aria-live='polite'
    >
      <div className='specific-copilot-freshness__content'>
        <strong>{t(`freshness.${status}_title`)}</strong>
        {analyzedAt && <span>{t('freshness.generated_at', {date: analyzedAt})}</span>}
        <small>
          {checking
            ? t('freshness.checking_body')
            : status === 'updates_available'
            ? t('freshness.updates_available_body', {
                sources: changedSources.join(', '),
              })
            : t(`freshness.${status}_body`)}
        </small>
      </div>
      <button
        type='button'
        className='btn btn-sm btn-light-primary specific-copilot-freshness__action'
        onClick={onRefresh}
        disabled={loading || checking}
      >
        {(loading || checking) && (
          <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />
        )}
        {checking
          ? t('freshness.checking')
          : loading
          ? t('freshness.updating')
          : t('freshness.update')}
      </button>
    </section>
  )
}

const AnalysisPersistenceNotice = ({analysis, loading, onRetry}) => {
  const {t} = useTranslation('copilot')
  const persistence = analysis?.analysisMetadata?.persistence || {}

  if (persistence.status !== 'failed') {
    return null
  }

  return (
    <section className='specific-copilot-persistence-error' role='alert' aria-live='polite'>
      <div className='specific-copilot-persistence-error__content'>
        <strong>{t('persistence.failed_title')}</strong>
        <span>{t('persistence.failed_body')}</span>
      </div>
      <button
        type='button'
        className='btn btn-sm btn-light-danger specific-copilot-persistence-error__action'
        onClick={onRetry}
        disabled={loading}
      >
        {loading && <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />}
        {loading ? t('persistence.retrying') : t('persistence.retry')}
      </button>
    </section>
  )
}

const LayerCatalog = ({layers}) => {
  const {t} = useTranslation('copilot')

  return (
    <section className='specific-copilot-layers' aria-labelledby='copilot-layers-title'>
      <div className='specific-copilot-section-heading'>
        <h4 id='copilot-layers-title'>{t('layers_title')}</h4>
        <p>{t('layers_notice')}</p>
      </div>
      <div className='specific-copilot-layers__grid'>
        {layers.map((layer) => (
          <article
            key={layer.id}
            className={`specific-copilot-layer specific-copilot-layer--${
              layer.status || 'unavailable'
            }`}
          >
            <span className='specific-copilot-layer__indicator' aria-hidden='true' />
            <div>
              <strong>{t(`layers.${layer.id}`)}</strong>
              <small>{t(`source_status.${layer.status || 'unavailable'}`)}</small>
            </div>
            {Number.isFinite(Number(layer.count)) && (
              <span className='specific-copilot-layer__count'>{layer.count}</span>
            )}
          </article>
        ))}
      </div>
    </section>
  )
}

export const buildCopilotContextKey = ({landId, reportId, language}) =>
  [landId || '', reportId || '', language || 'es'].join('|')

const ResultInterpretation = ({items, aiInterpretation}) => {
  const {t} = useTranslation('copilot')
  const observedItems = Array.isArray(items) ? items : []
  const aiStatus = aiInterpretation?.status || 'not_requested'
  const aiText = typeof aiInterpretation?.text === 'string' ? aiInterpretation.text.trim() : ''
  const aiParagraphs = aiText ? aiText.split(/\n+/).filter(Boolean) : []
  const hasAiResult = aiStatus === 'available' && aiParagraphs.length > 0
  const showAiSection = hasAiResult || aiStatus !== 'not_requested'

  if (observedItems.length === 0 && !showAiSection) {
    return null
  }

  return (
    <section
      className='specific-copilot-interpretation'
      aria-labelledby='copilot-interpretation-title'
    >
      <div className='specific-copilot-section-heading'>
        <h4 id='copilot-interpretation-title'>{t('interpretation.title')}</h4>
        <p>{t('interpretation.subtitle')}</p>
      </div>
      {observedItems.length > 0 && (
        <div className='specific-copilot-interpretation__block'>
          <h5>{t('interpretation.observed_title')}</h5>
          <div className='specific-copilot-interpretation__text'>
            {observedItems.map((item) => (
              <p key={item.id}>{item.text || t(item.key, item.values)}</p>
            ))}
          </div>
        </div>
      )}
      {showAiSection && (
        <div className='specific-copilot-interpretation__block'>
          <h5>{t('interpretation.ai_title')}</h5>
          {hasAiResult ? (
            <div
              className={
                'specific-copilot-interpretation__text ' +
                'specific-copilot-interpretation__text--ai'
              }
            >
              {aiParagraphs.map((paragraph, index) => (
                <p key={`${index}-${paragraph.slice(0, 24)}`}>{paragraph}</p>
              ))}
            </div>
          ) : (
            <p className='specific-copilot-interpretation__status'>
              {t('interpretation.ai_unavailable')}
            </p>
          )}
        </div>
      )}
      <p className='specific-copilot-interpretation__notice'>{t('interpretation.notice')}</p>
    </section>
  )
}

const FindingEvidence = ({evidence}) => {
  if (!Array.isArray(evidence) || evidence.length === 0) {
    return null
  }

  return (
    <dl className='specific-copilot-evidence'>
      {evidence.map((item, index) => (
        <div key={`${item.label || 'evidence'}-${index}`}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

const Finding = ({finding, onAction}) => {
  const {t} = useTranslation('copilot')
  const actionLabelKey = getCopilotActionLabelKey(finding.action)
  const tone = getCopilotFindingTone(finding.severity)

  return (
    <article className={`specific-copilot-finding specific-copilot-finding--${tone}`}>
      <div className='specific-copilot-finding__heading'>
        <span className='specific-copilot-finding__marker' aria-hidden='true' />
        <div>
          <h4>{finding.title}</h4>
          <p>{finding.summary}</p>
        </div>
      </div>

      <FindingEvidence evidence={finding.evidence} />

      {actionLabelKey && typeof onAction === 'function' && (
        <button
          type='button'
          className='btn btn-sm btn-light-primary specific-copilot-finding__action'
          onClick={() => onAction(finding.action)}
        >
          {t(actionLabelKey)}
        </button>
      )}
    </article>
  )
}

const EconomicReportFinding = ({findings, onAction}) => {
  const {t} = useTranslation('copilot')
  const action = findings.find((finding) => finding.action?.type === 'open_report')?.action
  const severity = ['warning', 'attention', 'positive'].find((candidate) =>
    findings.some((finding) => finding.severity === candidate)
  )
  const tone = getCopilotFindingTone(severity)
  const actionLabelKey = getCopilotActionLabelKey(action)

  return (
    <article
      className={`specific-copilot-finding specific-copilot-finding--economic-report specific-copilot-finding--${tone}`}
    >
      <div className='specific-copilot-finding__heading'>
        <span className='specific-copilot-finding__marker' aria-hidden='true' />
        <h4>{t('economic_report_finding_title')}</h4>
      </div>

      <div className='specific-copilot-economic-report-signals'>
        {findings.map((finding) => (
          <section className='specific-copilot-economic-report-signal' key={finding.id}>
            <h5>{finding.title}</h5>
            <p>{finding.summary}</p>
            <FindingEvidence evidence={finding.evidence} />
          </section>
        ))}
      </div>

      {actionLabelKey && typeof onAction === 'function' && (
        <button
          type='button'
          className='btn btn-sm btn-light-primary specific-copilot-finding__action'
          onClick={() => onAction(action)}
        >
          {t(actionLabelKey)}
        </button>
      )}
    </article>
  )
}

const AnalysisLoading = () => {
  const {t} = useTranslation('copilot')

  return (
    <div className='specific-copilot-loading' role='status'>
      <span className='spinner-border spinner-border-sm' aria-hidden='true' />
      <span>{t('loading')}</span>
    </div>
  )
}

const CopilotConversation = ({
  analysis,
  question,
  setQuestion,
  loading,
  showAnswer,
  askedQuestion,
  collapsed,
  onToggleCollapsed,
  onSubmit,
}) => {
  const {t} = useTranslation('copilot')
  const [answerCopied, setAnswerCopied] = useState(false)
  const copyFeedbackTimerRef = useRef(null)
  const hasAnswer = Boolean(showAnswer && analysis?.answer)

  useEffect(() => {
    setAnswerCopied(false)

    return () => {
      if (copyFeedbackTimerRef.current) {
        clearTimeout(copyFeedbackTimerRef.current)
      }
    }
  }, [analysis?.answer])

  const showCopyFeedback = () => {
    setAnswerCopied(true)

    if (copyFeedbackTimerRef.current) {
      clearTimeout(copyFeedbackTimerRef.current)
    }

    copyFeedbackTimerRef.current = setTimeout(() => {
      setAnswerCopied(false)
    }, 1800)
  }

  const copyAnswer = async () => {
    if (!analysis?.answer || !navigator.clipboard?.writeText) {
      return
    }

    try {
      await navigator.clipboard.writeText(analysis.answer)
      showCopyFeedback()
    } catch (copyError) {
      setAnswerCopied(false)
    }
  }

  return (
    <section
      className={`specific-copilot-conversation ${
        hasAnswer ? 'specific-copilot-conversation--answered' : ''
      } ${hasAnswer && collapsed ? 'specific-copilot-conversation--collapsed' : ''}`.trim()}
      aria-label={t('question_label')}
    >
      {hasAnswer && (
        <div className='specific-copilot-conversation__toolbar'>
          <button
            type='button'
            className='specific-copilot-conversation__toggle'
            onClick={onToggleCollapsed}
            aria-expanded={!collapsed}
            aria-controls='specific-copilot-conversation-content'
            title={collapsed ? t('expand_conversation') : t('collapse_conversation')}
          >
            <span className='specific-copilot-conversation__summary'>
              <strong>{t('conversation_title')}</strong>
              {askedQuestion && <small>{askedQuestion}</small>}
            </span>
            <KTSVG
              path={
                collapsed
                  ? '/media/icons/duotune/arrows/arr071.svg'
                  : '/media/icons/duotune/arrows/arr072.svg'
              }
              className='svg-icon svg-icon-2'
            />
          </button>

          <button
            type='button'
            className={`specific-copilot-answer__copy ${
              answerCopied ? 'specific-copilot-answer__copy--copied' : ''
            }`.trim()}
            onClick={copyAnswer}
            aria-label={answerCopied ? t('answer_copied') : t('copy_answer')}
            title={answerCopied ? t('answer_copied') : t('copy_answer')}
          >
            {answerCopied ? <CheckIcon aria-hidden='true' /> : <CopyIcon aria-hidden='true' />}
            <span className='visually-hidden' aria-live='polite'>
              {answerCopied ? t('answer_copied') : ''}
            </span>
          </button>
        </div>
      )}

      <div
        id='specific-copilot-conversation-content'
        className='specific-copilot-conversation__content'
        hidden={hasAnswer && collapsed}
      >
        {hasAnswer && (
          <section
            className='specific-copilot-answer'
            aria-labelledby='copilot-answer-title'
            aria-live='polite'
          >
            <h4 id='copilot-answer-title'>{t('answer_title')}</h4>
            <p>{analysis.answer}</p>
          </section>
        )}

        <form className='specific-copilot-question' onSubmit={onSubmit}>
          {analysis?.notice && <p className='specific-copilot-notice'>{analysis.notice}</p>}
          <label htmlFor='specific-copilot-question'>{t('question_label')}</label>
          <div className='specific-copilot-question__controls'>
            <input
              id='specific-copilot-question'
              className='form-control'
              type='text'
              value={question}
              onChange={(event) => setQuestion(event.target.value.slice(0, 600))}
              placeholder={t('question_placeholder')}
              disabled={loading || !analysis}
              autoComplete='off'
            />
            <button
              type='submit'
              className='btn btn-primary'
              disabled={loading || !analysis || !question.trim()}
            >
              {loading && (
                <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />
              )}
              {t('ask')}
            </button>
          </div>
        </form>
      </div>
    </section>
  )
}

const SpecificCopilot = ({open, setOpen, landId, reportId = null, onAction}) => {
  const {t, i18n} = useTranslation('copilot')
  const [analysis, setAnalysis] = useState(null)
  const [question, setQuestion] = useState('')
  const [loading, setLoading] = useState(false)
  const [freshnessLoading, setFreshnessLoading] = useState(false)
  const [error, setError] = useState('')
  const [showAnswer, setShowAnswer] = useState(false)
  const [askedQuestion, setAskedQuestion] = useState('')
  const [conversationCollapsed, setConversationCollapsed] = useState(false)
  const [detailsExpanded, setDetailsExpanded] = useState(false)
  const [pdfDownloading, setPdfDownloading] = useState(false)
  const [pdfProgressStage, setPdfProgressStage] = useState('')
  const [markdownDownloading, setMarkdownDownloading] = useState(false)
  const [agronomicProfile, setAgronomicProfile] = useState(EMPTY_PROFILE)
  const requestIdRef = useRef(0)
  const freshnessRequestIdRef = useRef(0)
  const agronomicProfileRef = useRef(EMPTY_PROFILE)
  const modalContextRef = useRef('')
  const modalContentRef = useRef(null)

  useEffect(() => {
    agronomicProfileRef.current = agronomicProfile
  }, [agronomicProfile])

  const checkCachedSourceFreshness = useCallback(
    async (profileOverride = null, expectedAnalysisRunId = '') => {
      if (!landId) {
        return
      }

      const requestId = ++freshnessRequestIdRef.current
      const resolvedProfile = profileOverride || agronomicProfileRef.current
      const hasCompleteProfile = Boolean(
        resolvedProfile?.crop && resolvedProfile?.genotypeId && resolvedProfile?.sowingDate
      )
      setFreshnessLoading(true)

      try {
        const response = await LandService.analyzeWithCopilot(landId, {
          question: '',
          reportId: reportId || null,
          locale: i18n.language || 'es',
          agronomicProfile: hasCompleteProfile
            ? {...resolvedProfile, harvestDate: resolvedProfile.harvestDate || null}
            : null,
          reuseCachedAnalysis: true,
          checkSourceFreshness: true,
        })
        if (requestId !== freshnessRequestIdRef.current || !response?.ok) {
          return
        }

        const refreshed = normalizeCopilotAnalysis(response.data, i18n.language)
        setAnalysis((current) => {
          if (!current) {
            return current
          }
          const currentRunId = current.analysisMetadata?.persistence?.analysisRunId || ''
          if (expectedAnalysisRunId && currentRunId && currentRunId !== expectedAnalysisRunId) {
            return current
          }
          return {
            ...current,
            analysisMetadata: {
              ...current.analysisMetadata,
              sourceFreshness: refreshed.analysisMetadata?.sourceFreshness,
            },
          }
        })
      } catch (requestError) {
        // Freshness is advisory. The persisted analysis remains immediately usable.
      } finally {
        if (requestId === freshnessRequestIdRef.current) {
          setFreshnessLoading(false)
        }
      }
    },
    [i18n.language, landId, reportId]
  )

  const runAnalysis = useCallback(
    async (nextQuestion = '', profileOverride = null, {reuseCachedAnalysis = false} = {}) => {
      if (!landId) {
        return
      }

      const requestId = ++requestIdRef.current
      setLoading(true)
      setError('')

      const resolvedProfile = profileOverride || agronomicProfileRef.current
      const hasCompleteProfile = Boolean(
        resolvedProfile?.crop && resolvedProfile?.genotypeId && resolvedProfile?.sowingDate
      )

      try {
        const response = await LandService.analyzeWithCopilot(landId, {
          question: nextQuestion.trim(),
          reportId: reportId || null,
          locale: i18n.language || 'es',
          agronomicProfile: hasCompleteProfile
            ? {
                ...resolvedProfile,
                harvestDate: resolvedProfile.harvestDate || null,
              }
            : null,
          reuseCachedAnalysis,
        })

        if (requestId !== requestIdRef.current) {
          return
        }

        if (!response?.ok) {
          setError(t('errors.generic'))
          return
        }

        const normalizedAnalysis = normalizeCopilotAnalysis(response.data, i18n.language)
        setAnalysis(normalizedAnalysis)
        setShowAnswer(Boolean(nextQuestion.trim()))
        if (nextQuestion.trim()) {
          setAskedQuestion(nextQuestion.trim())
          setConversationCollapsed(false)
        }
        if (hasCompleteAgronomicProfile(normalizedAnalysis.agronomicProfile)) {
          setAgronomicProfile(normalizedAnalysis.agronomicProfile)
          agronomicProfileRef.current = normalizedAnalysis.agronomicProfile
        }
        if (
          reuseCachedAnalysis &&
          !nextQuestion.trim() &&
          normalizedAnalysis.analysisMetadata?.reused
        ) {
          const analysisRunId =
            normalizedAnalysis.analysisMetadata?.persistence?.analysisRunId || ''
          void checkCachedSourceFreshness(normalizedAnalysis.agronomicProfile, analysisRunId)
        }
      } catch (requestError) {
        if (requestId !== requestIdRef.current) {
          return
        }

        const status = requestError?.response?.status
        const code = requestError?.response?.data?.code
        setError(
          status === 429
            ? t('errors.rate_limit')
            : status === 503 && code === 'copilot_analysis_storage_unavailable'
            ? t('errors.storage_unavailable')
            : t('errors.generic')
        )
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false)
        }
      }
    },
    [checkCachedSourceFreshness, i18n.language, landId, reportId, t]
  )

  useEffect(() => {
    if (!open) {
      requestIdRef.current += 1
      freshnessRequestIdRef.current += 1
      setFreshnessLoading(false)
      modalContextRef.current = ''
      return
    }

    const contextKey = buildCopilotContextKey({
      landId,
      reportId,
      language: i18n.language || 'es',
    })
    if (modalContextRef.current === contextKey) {
      return
    }

    modalContextRef.current = contextKey
    requestIdRef.current += 1
    freshnessRequestIdRef.current += 1
    setLoading(false)
    setFreshnessLoading(false)
    setQuestion('')
    setShowAnswer(false)
    setAskedQuestion('')
    setConversationCollapsed(false)
    setDetailsExpanded(false)
    setAnalysis(null)
    setAgronomicProfile(EMPTY_PROFILE)
    agronomicProfileRef.current = EMPTY_PROFILE
    runAnalysis('', null, {reuseCachedAnalysis: true})
  }, [open, landId, reportId, i18n.language, runAnalysis])

  const submitQuestion = (event) => {
    event.preventDefault()
    const nextQuestion = question.trim()

    if (!nextQuestion || loading) {
      return
    }

    runAnalysis(
      nextQuestion,
      getAgronomicProfileForQuestion(analysis, agronomicProfileRef.current),
      {
        reuseCachedAnalysis: true,
      }
    )
  }

  const analyzeAgronomicProfile = (profile) => {
    const nextProfile = {
      crop: profile.crop,
      genotypeId: profile.genotypeId,
      sowingDate: profile.sowingDate,
      harvestDate: profile.harvestDate || '',
      thermalTime: profile.thermalTime,
    }
    setAgronomicProfile(nextProfile)
    runAnalysis('', nextProfile)
  }

  const refreshAnalysis = () => {
    if (loading) {
      return
    }

    const currentProfile = agronomicProfileRef.current
    setQuestion('')
    setShowAnswer(false)
    setAskedQuestion('')
    setConversationCollapsed(false)
    runAnalysis('', hasCompleteAgronomicProfile(currentProfile) ? currentProfile : null, {
      reuseCachedAnalysis: false,
    })
  }

  const close = () => {
    requestIdRef.current += 1
    freshnessRequestIdRef.current += 1
    setOpen(false)
  }

  const scopeLabel = [analysis?.scope?.fieldName, analysis?.scope?.landName]
    .filter(Boolean)
    .join(' - ')
  const hasAgronomicResult = hasAvailableAgronomicAnalysis(analysis)
  const groupedFindings = groupCopilotFindings(analysis?.findings)
  const resultInterpretation = buildCopilotResultInterpretation(analysis, i18n.language || 'es')

  const downloadPdf = async () => {
    if (!analysis || !modalContentRef.current || pdfDownloading || markdownDownloading) {
      return
    }

    setPdfDownloading(true)
    setPdfProgressStage('preparing_visuals')
    setError('')

    try {
      await downloadSpecificCopilotPdf({
        analysis,
        rootElement: modalContentRef.current,
        interpretation: resultInterpretation,
        question: showAnswer ? askedQuestion : '',
        answer: showAnswer ? analysis.answer : '',
        t,
        locale: i18n.language || 'es',
        onProgress: ({stage}) => setPdfProgressStage(stage),
      })
    } catch (pdfError) {
      setError(t('pdf.error'))
    } finally {
      setPdfDownloading(false)
      setPdfProgressStage('')
    }
  }

  const downloadMarkdown = async () => {
    if (!analysis || markdownDownloading || pdfDownloading) {
      return
    }

    setMarkdownDownloading(true)
    setError('')

    try {
      await downloadSpecificCopilotMarkdown({
        analysis,
        interpretation: resultInterpretation.map((item) => ({
          ...item,
          text: item.text || t(item.key, item.values),
        })),
        question: showAnswer ? askedQuestion : '',
        answer: showAnswer ? analysis.answer : '',
        t,
        locale: i18n.language || 'es',
      })
    } catch (markdownError) {
      setError(t('markdown.error'))
    } finally {
      setMarkdownDownloading(false)
    }
  }

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      className='specific-copilot-modal'
      backdropClassName='specific-copilot-backdrop'
    >
      <div
        className={`modal-dialog modal-dialog-centered specific-copilot-dialog ${
          hasAgronomicResult
            ? 'modal-lg specific-copilot-dialog--analysis'
            : 'specific-copilot-dialog--setup'
        }`.trim()}
      >
        <div ref={modalContentRef} className='modal-content specific-copilot-content'>
          <div className='modal-header specific-copilot-header'>
            <div className='specific-copilot-header__identity'>
              <span className='specific-copilot-header__icon' aria-hidden='true'>
                <ChatGptLogo className='specific-copilot-header__logo' />
              </span>
              <div>
                <h3>{t('title')}</h3>
                <p>{scopeLabel || t('context_fallback')}</p>
              </div>
            </div>
            <div className='specific-copilot-header__actions'>
              {analysis && hasAgronomicResult && (
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary specific-copilot-header__download'
                  onClick={downloadPdf}
                  disabled={loading || pdfDownloading || markdownDownloading}
                >
                  {pdfDownloading && (
                    <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />
                  )}
                  {pdfDownloading
                    ? t(`pdf.stages.${pdfProgressStage}`, {defaultValue: t('pdf.generating')})
                    : t('pdf.download')}
                </button>
              )}
              {analysis && hasAgronomicResult && (
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary specific-copilot-header__download'
                  onClick={downloadMarkdown}
                  disabled={loading || pdfDownloading || markdownDownloading}
                >
                  {markdownDownloading && (
                    <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />
                  )}
                  {markdownDownloading ? t('markdown.generating') : t('markdown.download')}
                </button>
              )}
              <button
                type='button'
                className='btn btn-icon btn-sm btn-active-light-primary'
                onClick={close}
                aria-label={t('close')}
                title={t('close')}
              >
                <KTSVG
                  path='/media/icons/duotune/arrows/arr061.svg'
                  className='svg-icon svg-icon-2'
                />
              </button>
            </div>
          </div>

          <div className='modal-body specific-copilot-body'>
            {loading && !analysis && <AnalysisLoading />}

            {error && !analysis && (
              <div className='specific-copilot-error' role='alert'>
                <strong>{t('errors.title')}</strong>
                <span>{error}</span>
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary'
                  onClick={() =>
                    runAnalysis(question, null, {reuseCachedAnalysis: Boolean(question.trim())})
                  }
                >
                  {t('retry')}
                </button>
              </div>
            )}

            {analysis && hasAgronomicResult && (
              <>
                <AnalysisPersistenceNotice
                  analysis={analysis}
                  loading={loading}
                  onRetry={refreshAnalysis}
                />
                <AnalysisFreshness
                  analysis={analysis}
                  loading={loading}
                  checking={freshnessLoading}
                  onRefresh={refreshAnalysis}
                />
              </>
            )}

            {error && analysis && (
              <div className='specific-copilot-inline-error' role='alert'>
                <strong>{t('errors.title')}</strong>
                <span>{error}</span>
              </div>
            )}

            {analysis && !hasAgronomicResult && (
              <section className='specific-copilot-setup'>
                <SpecificCopilotAgronomy
                  open={open}
                  analysis={analysis.agronomicAnalysis}
                  catalog={analysis.agronomicCatalog}
                  recentNdvi={analysis.recentNdvi}
                  recentEvi={analysis.recentEvi}
                  profile={agronomicProfile}
                  setProfile={setAgronomicProfile}
                  onSubmit={analyzeAgronomicProfile}
                  loading={loading}
                />
              </section>
            )}

            {analysis && hasAgronomicResult && (
              <>
                <CopilotConversation
                  analysis={analysis}
                  question={question}
                  setQuestion={setQuestion}
                  loading={loading}
                  showAnswer={showAnswer}
                  askedQuestion={askedQuestion}
                  collapsed={conversationCollapsed}
                  onToggleCollapsed={() => setConversationCollapsed((current) => !current)}
                  onSubmit={submitQuestion}
                />

                {loading && (
                  <div className='specific-copilot-result-header'>
                    <span className='spinner-border spinner-border-sm' role='status' />
                  </div>
                )}

                <section
                  className='specific-copilot-agronomy'
                  aria-labelledby='copilot-agronomy-title'
                >
                  <div className='specific-copilot-section-heading'>
                    <h4 id='copilot-agronomy-title'>{t('agronomy.title')}</h4>
                    <p>{t('agronomy.subtitle')}</p>
                  </div>
                  <SpecificCopilotAgronomy
                    open={open}
                    analysis={analysis.agronomicAnalysis}
                    catalog={analysis.agronomicCatalog}
                    recentNdvi={analysis.recentNdvi}
                    recentEvi={analysis.recentEvi}
                    profile={agronomicProfile}
                    setProfile={setAgronomicProfile}
                    onSubmit={analyzeAgronomicProfile}
                    loading={loading}
                  />
                </section>

                <SpecificCopilotIntegratedAnalysis analysis={analysis.crossLayerAnalysis} />

                <ResultInterpretation
                  items={resultInterpretation}
                  aiInterpretation={analysis.aiInterpretation}
                />

                {(analysis.layerCatalog.length > 0 || analysis.findings.length > 0) && (
                  <section className='specific-copilot-details'>
                    <button
                      type='button'
                      className='specific-copilot-details__toggle'
                      onClick={() => setDetailsExpanded((current) => !current)}
                      aria-expanded={detailsExpanded}
                      aria-controls='specific-copilot-details-content'
                      title={detailsExpanded ? t('collapse_details') : t('expand_details')}
                    >
                      <span className='specific-copilot-details__summary'>
                        <strong>{t('details_title')}</strong>
                        <small>
                          {t('details_summary', {
                            layers: analysis.layerCatalog.length,
                            findings: analysis.findings.length,
                          })}
                        </small>
                      </span>
                      <KTSVG
                        path={
                          detailsExpanded
                            ? '/media/icons/duotune/arrows/arr072.svg'
                            : '/media/icons/duotune/arrows/arr071.svg'
                        }
                        className='svg-icon svg-icon-2'
                      />
                    </button>

                    <div
                      id='specific-copilot-details-content'
                      className='specific-copilot-details__content'
                      hidden={!detailsExpanded}
                    >
                      {analysis.layerCatalog.length > 0 && (
                        <LayerCatalog layers={analysis.layerCatalog} />
                      )}

                      {analysis.findings.length > 0 && (
                        <section
                          className='specific-copilot-findings'
                          aria-labelledby='copilot-findings-title'
                        >
                          <div className='specific-copilot-section-heading'>
                            <h4 id='copilot-findings-title'>{t('findings_title')}</h4>
                          </div>
                          <div className='specific-copilot-findings__list'>
                            {groupedFindings.economicReport.length > 0 && (
                              <EconomicReportFinding
                                findings={groupedFindings.economicReport}
                                onAction={onAction}
                              />
                            )}
                            {groupedFindings.other.map((finding) => (
                              <Finding key={finding.id} finding={finding} onAction={onAction} />
                            ))}
                          </div>
                        </section>
                      )}
                    </div>
                  </section>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default SpecificCopilot
